import useStorage from '../../hooks/useStorage';
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetMediaSizes } from '../../hooks/useMediaSize';

export type RightSidebarProps = {
    header?: React.ReactNode;
    children?: React.ReactNode;
    metadata?: Record<string, string>;
};

export type LayoutContextProps = {
    isCollapsingLeftSidebar: boolean;
    setAutoPresetLeftSidebar: (ignore: boolean) => void;
    setIsRequiredDimension: (isRequired: boolean) => void;

    isLeftSidebarVisible: boolean;
    showLeftSidebar: () => void;
    hideLeftSidebar: () => void;

    /* Collapse and expand apply to desktop mode only */
    collapseLeftSidebar: () => void;
    expandLeftSidebar: () => void;

    isRightSidebarVisible: boolean;
    showRightSidebar: () => void;
    hideRightSidebar: () => void;

    isShowingTermsAndConditions?: boolean;
    setIsShowingTermsAndConditions: (isShowing: boolean) => void;

    /** Since there is no clear way of detecting wether  */
    hasCookieConsent?: boolean,
    setHasCookieConsent: (hasConsent: boolean) => void;
};

const notImplemented = () => { throw new Error('Not implemented'); };

const defaultLayout = {
    isCollapsingLeftSidebar: false,
    setAutoPresetLeftSidebar: notImplemented,
    setIsRequiredDimension: () => notImplemented,

    isLeftSidebarVisible: false,
    showLeftSidebar: notImplemented,
    hideLeftSidebar: notImplemented,

    collapseLeftSidebar: () => notImplemented,
    expandLeftSidebar: () => notImplemented,

    isRightSidebarVisible: false,
    showRightSidebar: notImplemented,
    hideRightSidebar: notImplemented,

    isShowingTermsAndConditions: false,
    setIsShowingTermsAndConditions: notImplemented,

    hasCookieConsent: false,
    setHasCookieConsent: notImplemented,
};

const LayoutContext = createContext(defaultLayout as LayoutContextProps);

const LayoutProvider = (props: PropsWithChildren) => {
    const location = useLocation();
    const cookieProps = useAttachCookieRenewListener();

    const [isShowingTermsAndConditions, setIsShowingTermsAndConditions] = useState(false);
    const [isRightSidebarVisible, setIsRightSidebarVisible] = useState(false);
    const [isLeftSidebarVisible, setIsLeftSidebarVisible] = useState(false);

    const { useStorageState: useUiStorageState } = useStorage('ui');
    const [isCollapsingLeftSidebar, setIsCollapsingLeftSidebar] = useUiStorageState<boolean>('sidebar-left-collapse', false);

    const showLeftSidebar = useCallback(() => setIsLeftSidebarVisible(true), []);
    const hideLeftSidebar = useCallback(() => setIsLeftSidebarVisible(false), []);

    const collapseLeftSidebar = useCallback(() => setIsCollapsingLeftSidebar(true), [setIsCollapsingLeftSidebar]);
    const expandLeftSidebar = useCallback(() => setIsCollapsingLeftSidebar(false), [setIsCollapsingLeftSidebar]);

    const showRightSidebar = useCallback(() => setIsRightSidebarVisible(true), []);
    const hideRightSidebar = useCallback(() => setIsRightSidebarVisible(false), []);

    const [autoPresetLeftSidebar, setAutoPresetLeftSidebar] = useState(false);

    const { isLarge } = useGetMediaSizes();
    const [isRequiredDimension, setIsRequiredDimension] = useState(isLarge);

    // Just to reduce complexity, We hide left sidebar if right sidebar is open ...
    const isLeftSidebarVisibleState = (!isLarge && isRightSidebarVisible) ? false : (autoPresetLeftSidebar ? autoPresetLeftSidebar : isRequiredDimension ? !isCollapsingLeftSidebar : isLeftSidebarVisible || autoPresetLeftSidebar);

    const value = useMemo(() => ({
        isCollapsingLeftSidebar,
        setIsRequiredDimension,
        setAutoPresetLeftSidebar,

        isLeftSidebarVisible: isLeftSidebarVisibleState,
        showLeftSidebar,
        hideLeftSidebar,
        collapseLeftSidebar,
        expandLeftSidebar,

        isRightSidebarVisible,
        showRightSidebar,
        hideRightSidebar,

        isShowingTermsAndConditions,
        setIsShowingTermsAndConditions,

        ...cookieProps,
    }), [isCollapsingLeftSidebar, isLeftSidebarVisibleState, showLeftSidebar, hideLeftSidebar, collapseLeftSidebar, expandLeftSidebar, isRightSidebarVisible, showRightSidebar, hideRightSidebar, isShowingTermsAndConditions, cookieProps]);

    // Close right sidebar upon location change
    const pathnameRef = useRef(location.pathname);
    useEffect(() => {
        if (pathnameRef.current && location.pathname === pathnameRef.current) return;
        pathnameRef.current = location.pathname;
        hideRightSidebar();
    }, [location, hideRightSidebar]);

    return <LayoutContext.Provider value={value}>
        {props.children}
    </LayoutContext.Provider>;
};

export default LayoutProvider;

export const useLayout = () => useContext(LayoutContext);

const useAttachCookieRenewListener = () => {
    const [hasCookieConsent, setHasCookieConsent] = useState<boolean | undefined>();
    return { hasCookieConsent, setHasCookieConsent };
};

// export const useLayoutAutoPresetLeftSidebar = ({ when }: { when: boolean; }) => {
//     const { setAutoPresetLeftSidebar } = useLayout();

//     useEffect(() => {
//         setAutoPresetLeftSidebar(when);
//         return () => { setAutoPresetLeftSidebar(false); };
//     }, [setAutoPresetLeftSidebar, when]);
// };